// Single source of truth for demo topics, subtopics, and video URLs.
// Adding a topic / video here automatically propagates to:
//   - DemoTopicsPage   (left-side topic cards)
//   - DemoPanel        (right-side selected list)
//   - DemoPlayerPage   (video lookup + sequential playback)
//
// Subtopic keys MUST be globally unique across all topics — they are used as
// flat identifiers in `selectedSubtopicKeys: string[]` state.

export interface DemoSubtopic {
  /** i18n key for the subtopic title */
  key: string
  /** Path under the S3 base, e.g. `shipping/rate_shopping.mp4` */
  videoPath: string
}

export interface DemoTopic {
  /** i18n key for the topic title (rendered as the main checkbox label) */
  key: string
  subtopics: DemoSubtopic[]
}

/**
 * S3 base for all demo videos. The full path layout uploaded to S3 is
 * `${base}/{lang}/shipbae/{topic}/{file}.mp4`. The `{lang}` segment varies
 * per UI language (en / ja / zh); `shipbae` is a fixed suffix shared by every
 * language.
 */
export const DEMO_VIDEO_BASE_URL =
  'https://gori-ai.s3.us-west-2.amazonaws.com/shipbae-assets/demo-videos'

/**
 * Languages that have a localized video set uploaded to S3. UI languages NOT
 * in this set fall back to English so users on ja/zh don't see 404s while
 * those translations are being recorded.
 *
 * To enable a new language: upload videos to `/demo-videos/{lang}/shipbae/...`
 * on S3 (matching the English layout), then add the language code here.
 */
export const LANGUAGES_WITH_VIDEOS: ReadonlySet<string> = new Set(['en', 'zh', 'ja'])

/** Intro video path (relative to the per-language `${base}/{lang}/shipbae/` root). */
export const DEMO_INTRO_VIDEO_PATH = 'shipbae_intro.mp4'

/**
 * Build full S3 video URL for a given relative path + UI language.
 *
 * The language is replaced by `'en'` whenever it is not in
 * `LANGUAGES_WITH_VIDEOS`, so users on partially-translated languages still
 * get a working video stream instead of 404s.
 */
export const buildDemoVideoUrl = (videoPath: string, language: string): string => {
  const lang = LANGUAGES_WITH_VIDEOS.has(language) ? language : 'en'
  return `${DEMO_VIDEO_BASE_URL}/${lang}/shipbae/${videoPath}`
}

export const DEMO_TOPICS: DemoTopic[] = [
  {
    key: 'DEMO_TOPIC_CORE_SHIPPING',
    subtopics: [
      {
        key: 'DEMO_SUBTOPIC_RATE_SHOPPING',
        videoPath: 'shipping/rate_shopping.mp4',
      },
      {
        key: 'DEMO_SUBTOPIC_CREATE_LABELS_BY_UPLOAD',
        videoPath: 'shipping/create_labels_by_upload.mp4',
      },
      {
        key: 'DEMO_SUBTOPIC_INTERNATIONAL_SHIPPING_RATE',
        videoPath: 'shipping/international_shipping_rate.mp4',
      },
    ],
  },
  {
    key: 'DEMO_TOPIC_ORDER_MANAGEMENT',
    subtopics: [
      {
        key: 'DEMO_SUBTOPIC_CONNECT_STORES',
        videoPath: 'orders/connect_stores.mp4',
      },
      {
        key: 'DEMO_SUBTOPIC_MANAGE_AND_CREATE_LABELS',
        videoPath: 'orders/manage_and_create_labels.mp4',
      },
      {
        key: 'DEMO_SUBTOPIC_FILTER_ORDERS',
        videoPath: 'orders/filter_orders.mp4',
      },
    ],
  },
  {
    key: 'DEMO_TOPIC_AUTOMATION',
    subtopics: [
      {
        key: 'DEMO_SUBTOPIC_CREATE_PRESET',
        videoPath: 'automation/create_preset.mp4',
      },
    ],
  },
  {
    key: 'DEMO_TOPIC_USER_BUSINESS',
    subtopics: [
      {
        key: 'DEMO_SUBTOPIC_ADD_USER_WITH_PERMISSIONS',
        videoPath: 'user-business/add_user_with_permissions.mp4',
      },
      {
        key: 'DEMO_SUBTOPIC_SAVE_SHIP_FROM_ADDRESS',
        videoPath: 'user-business/save_ship_from_address.mp4',
      },
    ],
  },
  {
    key: 'DEMO_TOPIC_CUSTOMER_EXPERIENCE',
    subtopics: [
      {
        key: 'DEMO_SUBTOPIC_SHIPMENT_NOTIFICATIONS',
        videoPath: 'customer/setup_shipment_notifications.mp4',
      },
      {
        key: 'DEMO_SUBTOPIC_CUSTOM_PACKING_SLIPS',
        videoPath: 'customer/setup_custom_packing_slips.mp4',
      },
    ],
  },
  {
    key: 'DEMO_TOPIC_ANALYTICS',
    subtopics: [
      {
        key: 'DEMO_SUBTOPIC_SHIPMENT_REPORT',
        videoPath: 'analytics/shipment_report.mp4',
      },
      {
        key: 'DEMO_SUBTOPIC_BALANCE_TRANSACTIONS_REPORT',
        videoPath: 'analytics/balance_transactions_report.mp4',
      },
      {
        key: 'DEMO_SUBTOPIC_RETURN_SHIPMENT_REPORT',
        videoPath: 'analytics/return_shipment_report.mp4',
      },
      {
        key: 'DEMO_SUBTOPIC_ENDING_BALANCE_REPORT',
        videoPath: 'analytics/ending_balance_report.mp4',
      },
      {
        key: 'DEMO_SUBTOPIC_ORDER_DETAIL_REPORT',
        videoPath: 'analytics/order_detail_report.mp4',
      },
      {
        key: 'DEMO_SUBTOPIC_DATA_REPORT',
        videoPath: 'analytics/data_report.mp4',
      },
      {
        key: 'DEMO_SUBTOPIC_EXPORT_CSV',
        videoPath: 'analytics/export_csv.mp4',
      },
    ],
  },
  {
    key: 'DEMO_TOPIC_CROSS_BORDER_A1',
    subtopics: [
      {
        key: 'DEMO_SUBTOPIC_A1_INTRODUCTION',
        videoPath: 'a1/a1_introduction.mp4',
      },
      {
        key: 'DEMO_TOPICS_SUBTOPICS_CREATE_BATCH',
        videoPath: 'a1/create_batch.mp4',
      },
      {
        key: 'DEMO_TOPICS_SUBTOPICS_UPDATE_BATCH',
        videoPath: 'a1/update_batch_information.mp4',
      },
      {
        key: 'DEMO_TOPICS_SUBTOPICS_CREATE_LABELS',
        videoPath: 'a1/create_shipping_labels.mp4',
      },
      {
        key: 'DEMO_TOPICS_SUBTOPICS_NOTIFY_PARTNER',
        videoPath: 'a1/notify_origin_and_schedule_pickup.mp4',
      },
    ],
  },
]

/** Flat list of all subtopics in display order (used to enforce play order). */
export const DEMO_ALL_SUBTOPICS: DemoSubtopic[] = DEMO_TOPICS.flatMap((t) => t.subtopics)

/** O(1) subtopic-key → subtopic + parent-topic lookup. */
export const DEMO_SUBTOPIC_INDEX: Record<string, {subtopic: DemoSubtopic; topic: DemoTopic}> =
  DEMO_TOPICS.reduce((acc, topic) => {
    topic.subtopics.forEach((subtopic) => {
      acc[subtopic.key] = {subtopic, topic}
    })
    return acc
  }, {} as Record<string, {subtopic: DemoSubtopic; topic: DemoTopic}>)
